/* WHICH SHAPE THE APP IS IN.
 *
 * ═══ "PHONE-ISH" IS NOT A WIDTH ═══
 *
 * A phone in LANDSCAPE is 852 x 393 — wider than most laptops and shorter than
 * anything. Keyed on width alone it took the desktop layout: the panel opened
 * over half the chart, the scrubber went behind it and the tool column ran off
 * the bottom. Height is the constraint that actually bites there, so both are
 * asked. This query is `apps/web/src/mobile.svelte.ts`'s, moved verbatim,
 * because it was arrived at by measuring real devices and is the one part of
 * that file that must not be re-derived.
 *
 * ═══ AND THE POINTER IS A THIRD QUESTION ═══
 *
 * Neither of the queries below decides how big a target is. That is
 * `@media (pointer: coarse)`, asked by each component about itself, because a
 * touchscreen laptop and an iPad both want 44 px and neither is narrow — and
 * the boat's iPad is 1024 x 768, which matches no phone query anybody would
 * write. Keeping the two apart is what stopped the layers panel shipping 16 px
 * opacity sliders in landscape.
 */

/** Narrow OR short. See above — both arms are load-bearing. */
export const PHONEISH = '(max-width: 640px), (max-height: 500px)';

/** Wide enough for a rail and a chart, not wide enough for a docked panel
 *  column beside them: the panel becomes an overlay drawer instead.
 *
 *  ═══ 1024 IS INSIDE THIS, AND THAT IS THE WHOLE POINT ═══
 *
 *  This was `(max-width: 1023px)`, which is the number you write when you are
 *  thinking about breakpoints instead of about devices. An iPad in landscape is
 *  exactly 1024, so it took the DESKTOP layout — where a 56 px rail and a
 *  360 px docked column leave 608 px of chart, on the one machine in this
 *  project that is actually mounted at a chart table.
 *
 *  Caught by the shell harness, which asserts the shape each viewport resolves
 *  to rather than trusting the arithmetic. The boat's iPad is the reason this
 *  breakpoint exists; excluding it by one pixel is the kind of thing that
 *  survives review because the number looks deliberate. */
export const TABLETISH = '(max-width: 1024px)';

/** A phone held sideways: the sheet becomes a column down the right edge,
 *  because in landscape WIDTH is what you have. */
export const LANDSCAPE = '(max-height: 500px) and (min-width: 641px)';

export type Shape = 'phone' | 'tablet' | 'desktop';

class Layout {
  /* Reactive rather than read once: a rotation, or a desktop window dragged
   * narrow, has to move the panels rather than wait for a reload. The one thing
   * that is NOT reactive is which panel starts open — see panels.svelte.ts. */
  #phone = $state(false);
  #tablet = $state(false);
  #landscape = $state(false);

  get phone(): boolean {
    return this.#phone;
  }
  /** True on a tablet-width screen that is NOT a phone. The two queries overlap
   *  — a 640 px screen matches both — and every consumer wants "which one shape
   *  is this", so the overlap is resolved here rather than at each call site. */
  get tablet(): boolean {
    return this.#tablet && !this.#phone;
  }
  get desktop(): boolean {
    return !this.#phone && !this.#tablet;
  }
  /** A phone on its side. Only meaningful when `phone` is true. */
  get landscape(): boolean {
    return this.#phone && this.#landscape;
  }

  get shape(): Shape {
    if (this.#phone) return 'phone';
    if (this.#tablet) return 'tablet';
    return 'desktop';
  }

  constructor() {
    /* Guarded for the test runner and for any server-side render: `matchMedia`
     * does not exist there, and a design system that throws on import is one
     * nobody can unit-test. Everything defaults to desktop, which is the shape
     * with the fewest surprises when it is wrong. */
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const bind = (q: string, set: (v: boolean) => void) => {
      const mq = window.matchMedia(q);
      set(mq.matches);
      mq.addEventListener('change', (e) => set(e.matches));
    };
    bind(PHONEISH, (v) => (this.#phone = v));
    bind(TABLETISH, (v) => (this.#tablet = v));
    bind(LANDSCAPE, (v) => (this.#landscape = v));
  }
}

export const layout = new Layout();
